import React from 'react';

import { parseMoney } from '@betterment/core';
import { useSessionStorage } from '@betterment/js-runtime';
import { Box, Button, FormikField, H1, Input } from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import CoverageCopy from '../../../../Shared/CoverageCopy';
import { convertDisplayableErrors } from '../../../../Shared/displayable-errors';
import { ProductCategory } from '../../../data';
import { useCompleteDraftPurposeFlow } from '../../hooks/useCompleteDraftPurposeFlow';
import { customCoverage, useEmergencyFundStep } from '../FlowConfig';

type FormValues = Yup.InferType<typeof customCoverage>;

interface CustomCoverageInputProps {
  monthlyExpensesAmount: string;
}

const CustomCoverageInput: React.FC<CustomCoverageInputProps> = ({ monthlyExpensesAmount }) => {
  const { values } = useFormikContext<FormValues>();

  const target = parseMoney(values.targetAmount)?.cents ?? 0;
  const monthly = parseMoney(monthlyExpensesAmount)?.cents ?? 0;

  const coverage = monthly === 0 ? 0 : target / monthly;
  const coverageCopy = <CoverageCopy coverage={coverage} coverageMonthsCap={12} />;

  return (
    <FormikField name="targetAmount">
      <FormikField.Label>Custom savings target</FormikField.Label>
      <FormikField.Control>
        <Input appearance="default" spacing="default" mask="money" />
      </FormikField.Control>
      <FormikField.Description>{coverageCopy}</FormikField.Description>
      <FormikField.ErrorMessage />
    </FormikField>
  );
};

export const CustomCoverageStep: React.FC = () => {
  const navigate = useNavigate();
  const { dependencies: monthlyExpensesDependencies, values: monthlyExpensesValues } =
    useEmergencyFundStep('monthlyExpenses');
  const { commit, dependencies } = useEmergencyFundStep('customCoverage');
  const {
    monthlyExpenses: { monthlyExpensesAmount },
  } = dependencies;
  const { flowId } = useParams<{ flowId: string }>();

  // This is a temporary solution until we have a more permanent way to persist the product category
  // through different flows. We want to persist the product category so we can pass it to the AddGoalFlow.
  const { values: productCategory } = useSessionStorage<ProductCategory>('purpose-flow-product-category');

  const { completeFlow, errors: displayableErrors } = useCompleteDraftPurposeFlow({
    draftEnvelopeId: flowId,
    name: monthlyExpensesDependencies.nameAndImage.name,
    purposeSubType: monthlyExpensesDependencies.nameAndImage.purpose_sub_type,
    imageType: monthlyExpensesDependencies.nameAndImage.imageType,
    productCategory,
  });

  const handleSubmit = async (formValues: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    commit({ targetAmount: formValues.targetAmount });

    await completeFlow({
      targetAmount: parseMoney(formValues.targetAmount),
      purposeInput: {
        emergencyFund: {
          bufferPercent: 0,
          monthlyExpensesAmount: parseMoney(monthlyExpensesValues?.monthlyExpensesAmount ?? ''),
        },
      },
    });

    if (displayableErrors.length > 0) {
      formikHelpers.setErrors(convertDisplayableErrors(displayableErrors));
    }
  };

  return (
    <WorkflowLayout pageTitle="Emergency Fund Coverage">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate('/')} onBack={() => navigate(-1)} />
      <WorkflowLayout.Content>
        <Box css={{ mb: 'space.200' }}>
          <H1>How much do you want to save?</H1>
        </Box>

        <Formik
          onSubmit={handleSubmit}
          initialValues={{ targetAmount: '' }}
          validationSchema={customCoverage}
          validateOnChange={true}
        >
          <Form>
            <CustomCoverageInput monthlyExpensesAmount={monthlyExpensesAmount} />
            <Box css={{ mt: 'space.400' }}>
              <Button type="submit" variant="solidPrimary" css={{ mt: 'space.0' }}>
                Continue
              </Button>
            </Box>
          </Form>
        </Formik>
      </WorkflowLayout.Content>
    </WorkflowLayout>
  );
};
